import type { Messages } from '../i18n';

export interface Picker {
  start(): void;
  stop(): void;
  destroy(): void;
}

/** Injected into the host document while picking so every element shows a crosshair. */
const PICK_CURSOR_CSS = '*, *::before, *::after { cursor: crosshair !important; }';

export function createPicker(opts: {
  root: ShadowRoot;
  hostElement: HTMLElement;
  messages: Messages;
  onSelect(el: Element): void;
  onCancel(): void;
}): Picker {
  const highlight = document.createElement('div');
  highlight.className = 'bf-highlight';
  const label = document.createElement('div');
  label.className = 'bf-highlight-label';
  const hint = document.createElement('div');
  hint.className = 'bf-hint';
  hint.textContent = opts.messages.pickHint;
  opts.root.append(highlight, label, hint);

  let active = false;
  let current: Element | null = null;
  let throttleTimer: ReturnType<typeof setTimeout> | null = null;
  let lastUpdate = 0;
  let cursorStyle: HTMLStyleElement | null = null;

  function isWidgetTarget(target: EventTarget | null): boolean {
    return (
      target instanceof Node && (target === opts.hostElement || opts.hostElement.contains(target))
    );
  }

  function isPickable(el: Element): boolean {
    return el !== document.documentElement && el !== document.body && !isWidgetTarget(el);
  }

  function updateHighlight(): void {
    if (!active || !current) {
      highlight.style.display = 'none';
      label.style.display = 'none';
      return;
    }
    const rect = current.getBoundingClientRect();
    highlight.style.display = 'block';
    highlight.style.left = `${rect.left}px`;
    highlight.style.top = `${rect.top}px`;
    highlight.style.width = `${rect.width}px`;
    highlight.style.height = `${rect.height}px`;

    const tag = current.tagName.toLowerCase();
    const id = current.id ? `#${current.id}` : '';
    label.textContent = `${tag}${id} ${Math.round(rect.width)}×${Math.round(rect.height)}`;
    label.style.display = 'block';
    const labelTop = rect.top - 24;
    label.style.top = `${labelTop < 4 ? rect.bottom + 4 : labelTop}px`;
    label.style.left = `${Math.max(4, Math.min(rect.left, window.innerWidth - 160))}px`;
  }

  // Leading-edge time throttle (~1 frame). Deliberately not requestAnimationFrame:
  // rAF is suspended entirely in deprioritized/background tabs and embedded panes.
  function scheduleUpdate(): void {
    const elapsed = performance.now() - lastUpdate;
    if (elapsed >= 16) {
      lastUpdate = performance.now();
      updateHighlight();
      return;
    }
    if (throttleTimer === null) {
      throttleTimer = setTimeout(() => {
        throttleTimer = null;
        lastUpdate = performance.now();
        updateHighlight();
      }, 16 - elapsed);
    }
  }

  function onPointerMove(event: PointerEvent): void {
    const target = event.target;
    current = target instanceof Element && isPickable(target) ? target : null;
    scheduleUpdate();
  }

  /** Returns true when the event belongs to the page (and was swallowed). */
  function swallow(event: Event): boolean {
    if (isWidgetTarget(event.target)) return false;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    return true;
  }

  // No preventDefault here: canceling pointerdown suppresses the compatibility
  // mouse events (mousedown/mouseup/click) per spec — the click handler below
  // would never fire. Page handlers are still blocked via stopPropagation;
  // focus/drag/text-selection are prevented on mousedown instead.
  function onPointerDown(event: Event): void {
    if (isWidgetTarget(event.target)) return;
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  function onMouseButton(event: Event): void {
    swallow(event);
  }

  function onClick(event: MouseEvent): void {
    if (!swallow(event)) return;
    const target = event.target;
    if (target instanceof Element && isPickable(target)) {
      opts.onSelect(target);
    }
  }

  function onViewportChange(): void {
    scheduleUpdate();
  }

  function start(): void {
    if (active) return;
    active = true;
    current = null;
    hint.style.display = 'block';

    cursorStyle = document.createElement('style');
    cursorStyle.textContent = PICK_CURSOR_CSS;
    document.head.appendChild(cursorStyle);

    document.addEventListener('pointermove', onPointerMove, { capture: true, passive: true });
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('mousedown', onMouseButton, true);
    document.addEventListener('mouseup', onMouseButton, true);
    document.addEventListener('click', onClick, true);
    // Escape is not handled here: a document-level listener is already too late
    // to keep it away from the host page's own dismiss handlers, so the
    // controller claims it on `window` and calls onCancel from there.
    window.addEventListener('scroll', onViewportChange, { capture: true, passive: true });
    window.addEventListener('resize', onViewportChange);
  }

  function stop(): void {
    if (!active) return;
    active = false;
    current = null;
    hint.style.display = 'none';
    updateHighlight();

    cursorStyle?.remove();
    cursorStyle = null;

    document.removeEventListener('pointermove', onPointerMove, { capture: true });
    document.removeEventListener('pointerdown', onPointerDown, true);
    document.removeEventListener('mousedown', onMouseButton, true);
    document.removeEventListener('mouseup', onMouseButton, true);
    document.removeEventListener('click', onClick, true);
    window.removeEventListener('scroll', onViewportChange, { capture: true });
    window.removeEventListener('resize', onViewportChange);

    if (throttleTimer !== null) {
      clearTimeout(throttleTimer);
      throttleTimer = null;
    }
  }

  return {
    start,
    stop,
    destroy() {
      stop();
      highlight.remove();
      label.remove();
      hint.remove();
    },
  };
}
